/**
 * Sibling-app URLs for the unified-shell app switcher.
 *
 * These hostnames are public and get baked into the bundle at build time, so
 * they live in code rather than in a per-environment CI variable. The only
 * thing the environment supplies is *which* environment this is.
 *
 * Resolution order for the namespace:
 *   1. `VITE_NAMESPACE` — explicit, set by the image build.
 *   2. Derived from the `VITE_API_URL` host, so a build that predates the
 *      namespace var still lands somewhere sane.
 *   3. `dev`.
 *
 * Step 3 never yields `prod`: a misconfigured build must not hand an operator
 * a one-click path into production.
 */

export type Namespace = 'local' | 'dev' | 'stage' | 'prod';

export interface ISuiteUrls {
  coherence: string;
  teleop: string;
  platform: string;
}

/** Env keys this module reads. Mirrors the `VITE_*` subset of `import.meta.env`. */
export interface ISuiteUrlEnv {
  VITE_NAMESPACE?: string;
  VITE_API_URL?: string;
}

const namespaces: readonly Namespace[] = ['local', 'dev', 'stage', 'prod'];

/**
 * `app*.formant.io` is the LEGACY platform app; the current platform-app is
 * served from `platform*.formant.io` (its bundle injects
 * `__ENV__.APP === 'platform-app'`).
 *
 * Local ports come from each app's own dev script, not from platform-app's
 * config (which is stale): v2-coherence `vite --port 3005`, teleop
 * `config/local.env` PORT=3007, platform-app `vite --port 3001`. Platform and
 * this console both claim :3001 — override `CONSOLE_PORT` to run both.
 */
const suiteUrlsByNamespace: Record<Namespace, ISuiteUrls> = {
  local: {
    coherence: 'http://localhost:3005',
    teleop: 'http://localhost:3007',
    platform: 'http://localhost:3001',
  },
  dev: {
    coherence: 'https://coherence-dev.formant.io',
    teleop: 'https://teleop-dev.formant.io',
    platform: 'https://platform-dev.formant.io',
  },
  stage: {
    coherence: 'https://coherence-stage.formant.io',
    teleop: 'https://teleop-stage.formant.io',
    platform: 'https://platform-stage.formant.io',
  },
  prod: {
    coherence: 'https://coherence.formant.io',
    teleop: 'https://teleop.formant.io',
    platform: 'https://platform.formant.io',
  },
};

/** The exact prod API host. Matched literally — no suffix heuristic may reach `prod`. */
const prodApiHost = 'api.metaphysics.formant.ai';

function isNamespace(value: string): value is Namespace {
  return (namespaces as readonly string[]).includes(value);
}

function namespaceFromApiUrl(apiUrl: string): Namespace | null {
  let host: string;
  try {
    host = new URL(apiUrl).hostname;
  } catch {
    return null;
  }

  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return 'local';
  }
  if (host.includes('-dev.')) return 'dev';
  if (host.includes('-stage.')) return 'stage';
  if (host === prodApiHost) return 'prod';
  return null;
}

/** Named bots stay off stage and prod until the feature is ready there. */
export function showFormantBots(namespace: Namespace): boolean {
  return namespace === 'local' || namespace === 'dev';
}

export function resolveNamespace(env: ISuiteUrlEnv): Namespace {
  const explicit = env.VITE_NAMESPACE?.trim();
  if (explicit && isNamespace(explicit)) return explicit;

  const derived = env.VITE_API_URL?.trim() ? namespaceFromApiUrl(env.VITE_API_URL.trim()) : null;
  return derived ?? 'dev';
}

export function getSuiteUrls(env: ISuiteUrlEnv): ISuiteUrls {
  return suiteUrlsByNamespace[resolveNamespace(env)];
}

export const suiteUrls: ISuiteUrls = getSuiteUrls(import.meta.env as ISuiteUrlEnv);
